import { useState, useMemo } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { getBrandHost } from "@/lib/moduleHost";

const API = process.env.REACT_APP_BACKEND_URL || (typeof window !== "undefined" ? window.location.origin : "");
function formatDetail(d) {
  if (d == null) return "";
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((e) => e?.msg || JSON.stringify(e))
                                  .filter(Boolean).join(" ");
  if (typeof d === "object" && d.msg) return d.msg;
  return String(d);
}

export default function ResetPasswordPage() {
  const [params] = useSearchParams();
  const token = useMemo(() => params.get("token") || "", [params]);
  const nav = useNavigate();
  const [pw1, setPw1] = useState("");
  const [pw2, setPw2] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const tooShort = pw1.length > 0 && pw1.length < 8;
  const mismatch = pw2.length > 0 && pw1 !== pw2;
  const ready = pw1 && pw2 && !tooShort && !mismatch && token;

  async function onSubmit(e) {
    e.preventDefault();
    if (!ready) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/api/auth/reset-password`,
                        { token, new_password: pw1 },
                        { withCredentials: true });
      toast.success("Password updated. Sign in with your new password.");
      setTimeout(() => nav("/login", { replace: true }), 800);
    } catch (err) {
      toast.error(formatDetail(err?.response?.data?.detail)
                    || "This reset link is no longer valid.");
    }
    setSubmitting(false);
  }

  if (!token) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FDFBF7] px-4">
        <div className="w-full max-w-md bg-white rounded-2xl border border-[#E8E5DF] p-8 md:p-10"
              data-testid="reset-password-missing-token">
          <h1 className="font-serif-display text-2xl">Reset link missing</h1>
          <p className="text-sm text-[#5C625E] mt-3">
            This URL doesn&apos;t contain a valid reset token. Request a
            new link from the forgot-password page.
          </p>
          <Link to="/forgot-password"
                className="mt-6 inline-block text-[#C05746] hover:underline">
            Request a new link →
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#FDFBF7] px-4">
      <div
        data-testid="reset-password-card"
        className="w-full max-w-md bg-white rounded-2xl border border-[#E8E5DF] p-8 md:p-10"
      >
        <Link
          to="/"
          className="font-serif-display text-2xl text-[#121614] hover:text-[#C05746]"
        >
          {getBrandHost()?.title || "HustleSuite"}
        </Link>
        <h1 className="font-serif-display text-3xl md:text-4xl mt-6">
          Set a new password
        </h1>
        <p className="text-sm text-[#5C625E] mt-2">
          Choose something you haven&apos;t used before. Minimum 8 characters.
        </p>

        <form onSubmit={onSubmit} className="mt-8 space-y-4">
          <div>
            <Label htmlFor="reset-pw1">New password</Label>
            <Input
              id="reset-pw1"
              data-testid="reset-password-new"
              type="password"
              required
              value={pw1}
              onChange={(e) => setPw1(e.target.value)}
              className="mt-2 rounded-xl h-11"
              autoComplete="new-password"
            />
            {tooShort && (
              <p className="text-xs text-[#C05746] mt-1"
                  data-testid="reset-password-too-short">
                At least 8 characters, please.
              </p>
            )}
          </div>
          <div>
            <Label htmlFor="reset-pw2">Confirm password</Label>
            <Input
              id="reset-pw2"
              data-testid="reset-password-confirm"
              type="password"
              required
              value={pw2}
              onChange={(e) => setPw2(e.target.value)}
              className="mt-2 rounded-xl h-11"
              autoComplete="new-password"
            />
            {mismatch && (
              <p className="text-xs text-[#C05746] mt-1"
                  data-testid="reset-password-mismatch">
                Passwords don&apos;t match.
              </p>
            )}
          </div>
          <Button
            type="submit"
            data-testid="reset-password-submit"
            disabled={submitting || !ready}
            className="w-full bg-[#121614] text-[#FDFBF7] hover:bg-[#2C3448] h-11 rounded-xl"
          >
            {submitting ? "Updating…" : "Update password"}
          </Button>
          <div className="text-sm text-center">
            <Link to="/login" className="text-[#5C625E] hover:text-[#C05746]">
              Back to sign in
            </Link>
          </div>
        </form>
      </div>
    </div>
  );
}
